import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const FORM_SELECTOR = 'form[data-hic-nz-ajax-cart]';
const SUCCESS_DURATION = 2000;

function getSubmitButton(form) {
  return form.querySelector('button[type="submit"][name="add"]');
}

function getStatusElement(form) {
  let status = form.querySelector('[data-hic-nz-cart-status]');

  if (!status) {
    status = document.createElement('p');
    status.className = 'hic-nz-cart-status visually-hidden';
    status.setAttribute('data-hic-nz-cart-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    form.append(status);
  }

  return status;
}

function getButtonLabel(button) {
  return button.querySelector('[data-hic-nz-cart-label]') || button.querySelector('span:last-child');
}

function setStatus(form, message, isError = false) {
  const status = getStatusElement(form);
  status.textContent = message;
  status.classList.toggle('visually-hidden', !isError);
  status.classList.toggle('hic-nz-cart-status--error', isError);
}

function setButtonState(button, state) {
  const label = getButtonLabel(button);

  if (state === 'loading') {
    button.dataset.hicNzCartOriginalLabel = label?.textContent || '';
    button.dataset.hicNzCartState = 'loading';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-label', 'Adding to cart');
    return;
  }

  if (state === 'added') {
    button.dataset.hicNzCartState = 'added';
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.setAttribute('aria-label', 'Added to cart');
    if (label) label.textContent = 'Added to cart';

    let successIcon = button.querySelector('[data-hic-nz-cart-success-icon]');
    if (!successIcon) {
      successIcon = document.createElement('span');
      successIcon.className = 'hic-nz-cart-success-icon';
      successIcon.setAttribute('data-hic-nz-cart-success-icon', '');
      successIcon.setAttribute('aria-hidden', 'true');
      successIcon.textContent = String.fromCharCode(0x2713);
      button.prepend(successIcon);
    }
    return;
  }

  const originalLabel = button.dataset.hicNzCartOriginalLabel;
  if (label && originalLabel) label.textContent = originalLabel;
  button.dataset.hicNzCartState = '';
  button.disabled = false;
  button.removeAttribute('aria-busy');
  button.removeAttribute('aria-label');
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

async function addToCart(form) {
  const formData = new FormData(form);
  const variantId = formData.get('id');
  const quantity = Number(formData.get('quantity')) || 1;

  if (!variantId) {
    throw new Error('Please select an available product option.');
  }

  const sectionIds = getCartSectionIds();
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));
  formData.set('sections_url', window.location.pathname);

  const config = fetchConfig('javascript', { body: formData });
  const response = await fetch(Theme.routes.cart_add_url, {
    ...config,
    headers: {
      ...config.headers,
      Accept: 'application/json',
    },
  });
  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(data.message || data.description || 'Unable to add this product to your cart.');
  }

  return { data, variantId, quantity };
}

async function handleSubmit(event) {
  const form = event.target instanceof HTMLFormElement ? event.target : event.target.closest?.(FORM_SELECTOR);
  if (!form?.matches(FORM_SELECTOR)) return;

  event.preventDefault();
  if (form.dataset.hicNzCartSubmitting === 'true') return;

  const button = getSubmitButton(form);
  if (!button || button.disabled) return;

  form.dataset.hicNzCartSubmitting = 'true';
  setStatus(form, '');
  setButtonState(button, 'loading');

  try {
    const { data, variantId, quantity } = await addToCart(form);

    setStatus(form, 'Added to cart.');
    setButtonState(button, 'added');
    document.dispatchEvent(
      new CartAddEvent(data, form.id || '', {
        source: 'product-form-component',
        itemCount: quantity,
        productId: form.dataset.productId,
        variantId: String(variantId),
        sections: data.sections,
      })
    );

    window.clearTimeout(Number(form.dataset.hicNzCartResetTimer));
    form.dataset.hicNzCartResetTimer = String(
      window.setTimeout(() => {
        setButtonState(button, 'idle');
        setStatus(form, '');
      }, SUCCESS_DURATION)
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to add this product to your cart.';
    setButtonState(button, 'idle');
    setStatus(form, message, true);
    document.dispatchEvent(new CartErrorEvent(form.id || '', message, message, {}));
  } finally {
    form.dataset.hicNzCartSubmitting = 'false';
  }
}

document.addEventListener('submit', handleSubmit);
